/**
 * PivotPanel — the cross-study pivot list (Expansion Phase F): ranked
 * recruiting studies this contact may fit, with a per-criterion ✓/?/✗ strip
 * evaluated from what we already know (prescreen answers + the medical
 * profile). Shared by DisqualifyModal step 2 and the Screening tab card.
 *
 * Purple is the screening-family palette. Fetch failures render inline with a
 * Retry — the host always keeps its own exit visible (never trap a recruiter
 * mid-call).
 *
 * Accepting a pivot is one of the four places the randomized guard fires (plan
 * §5.2 site 4 — the /rescreen half; /attempts is the other). A 409 here means
 * the lead is still enrolled somewhere: the modal asks, and confirming re-posts
 * the identical rescreen with randomized_conflict_acknowledged:true.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Play } from 'lucide-react';
import api from '../../../services/api';
import { StudyMatch, StudyMatchCriterion, studyInternalLabel } from '../../types';
import { relTime } from '../../lib';
import RandomizedConflictModal from '../RandomizedConflictModal';

interface PivotPanelProps {
  contactId: string;
  /** Called AFTER a successful rescreen POST — host opens the runner. */
  onStart: (study: { id: string; title: string }) => void;
  /** In-flight rescreen signal — modal hosts lock their exits while true
   *  (a dismissal mid-POST would resurrect the runner when it resolves). */
  onBusyChange?: (busy: boolean) => void;
  /** The study being pivoted AWAY from (the just-screened-out study). Sent to
   *  the rescreen endpoint so the considering chip moves to the pivot target
   *  only when it was on this study — never clobbering a manual pin (#08). */
  fromStudyId?: string | null;
}

/** Human label for the contributing fact's source. */
const SOURCE_LABELS: Record<string, string> = {
  prescreen: 'prescreen',
  form: 'form',
  lead: 'web lead',
  profile: 'profile',
  // N-2: facts the texting agent captured (§6.8 fourth fact source).
  agent: 'AI agent',
};

const factValueLabel = (value: any): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const VERDICT_SQUARE: Record<string, string> = {
  pass: 'bg-green-500',
  fail: 'bg-red-500',
  unknown: 'bg-gray-300 dark:bg-gray-600',
};

const VERDICT_MARK: Record<string, { mark: string; cls: string }> = {
  pass: { mark: '✓', cls: 'text-green-600 dark:text-green-400' },
  fail: { mark: '✗', cls: 'text-red-600 dark:text-red-400' },
  unknown: { mark: '?', cls: 'text-gray-400 dark:text-gray-500' },
};

const VERDICT_SR: Record<string, string> = {
  pass: 'likely fits:',
  fail: "doesn't fit:",
  unknown: 'unknown:',
};

function CriterionRow({ c }: { c: StudyMatchCriterion }) {
  const v = VERDICT_MARK[c.verdict] || VERDICT_MARK.unknown;
  return (
    <li className="flex items-start gap-2 py-1.5">
      <span className={`w-4 flex-shrink-0 text-center text-sm font-semibold ${v.cls}`} aria-hidden="true">{v.mark}</span>
      <span className="sr-only">{VERDICT_SR[c.verdict] || VERDICT_SR.unknown}</span>
      <div className="min-w-0">
        <p className="text-xs text-gray-700 dark:text-gray-200">{c.display_question}</p>
        {c.fact ? (
          <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
            {factValueLabel(c.fact.value)} · {SOURCE_LABELS[c.fact.source] || c.fact.source}
            {c.fact.answered_at && <> · {relTime(c.fact.answered_at)}</>}
          </p>
        ) : (
          <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">Not asked yet</p>
        )}
      </div>
    </li>
  );
}

export default function PivotPanel({ contactId, onStart, onBusyChange, fromStudyId }: PivotPanelProps) {
  const [matches, setMatches] = useState<StudyMatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // A 409 randomized_conflict on the rescreen POST, with the match stashed so
  // confirming re-posts it acknowledged (plan §6.4).
  const [conflict, setConflict] = useState<{
    match: StudyMatch;
    randomizedStudyTitle: string | null;
    randomizedAt: string | null;
  } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setMatches(null);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/study-matches`);
      setMatches(res.data?.matches || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load study matches');
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const start = async (m: StudyMatch, acknowledged = false) => {
    setStartingId(m.study.id);
    setStartError(null);
    onBusyChange?.(true);
    try {
      await api.post(`/api/crm/contacts/${contactId}/rescreen`, {
        study_id: m.study.id,
        ...(fromStudyId ? { from_study_id: fromStudyId } : {}),
        ...(acknowledged ? { randomized_conflict_acknowledged: true } : {}),
      });
      onStart({ id: m.study.id, title: studyInternalLabel(m.study) });
      // Success unmounts this panel (the host swaps in the runner) — no state
      // writes here; the host clears its own busy flag in onStart.
    } catch (err: any) {
      const data = err?.response?.data;
      // Randomized elsewhere: ask instead of failing. Nothing is in flight while
      // the modal is open, so the host's exits are released — a dismissal there
      // can no longer resurrect a runner.
      if (err?.response?.status === 409 && data?.reason === 'randomized_conflict') {
        setConflict({
          match: m,
          randomizedStudyTitle: data.randomized_study?.title ?? null,
          randomizedAt: data.randomized_at ?? null,
        });
      } else {
        setStartError(data?.error || 'Could not start the prescreen');
      }
      setStartingId(null);
      onBusyChange?.(false);
    }
  };

  /** Re-post the rescreen the guard blocked, this time acknowledged. */
  const confirmConflict = () => {
    const pending = conflict;
    setConflict(null);
    if (pending) void start(pending.match, true);
  };

  /* Built here, rendered in the list branch below — a conflict can only exist
     once a match has been started, which means the list is what is on screen. */
  const conflictModal = conflict && (
    <RandomizedConflictModal
      targetStudyTitle={studyInternalLabel(conflict.match.study)}
      randomizedStudyTitle={conflict.randomizedStudyTitle}
      randomizedAt={conflict.randomizedAt}
      onCancel={() => setConflict(null)}
      onConfirm={confirmConflict}
    />
  );

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        <button
          type="button"
          onClick={load}
          className="mt-2 min-h-[44px] rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
        >
          Retry
        </button>
      </div>
    );
  }

  if (matches === null) {
    return (
      <div className="flex items-center gap-2 py-3">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Checking the other studies…</span>
      </div>
    );
  }

  if (matches.length === 0) {
    return <p className="py-2 text-sm text-gray-500 dark:text-gray-400">No other recruiting studies match yet.</p>;
  }

  return (
    <div>
      {conflictModal}
      {startError && <p className="mb-2 text-sm text-red-600 dark:text-red-400">{startError}</p>}
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {matches.map((m) => {
          const isOpen = expanded === m.study.id;
          const starting = startingId === m.study.id;
          return (
            <li key={m.study.id} className="py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : m.study.id)}
                  aria-expanded={isOpen}
                  className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-lg text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:min-h-0"
                >
                  {isOpen
                    ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{studyInternalLabel(m.study)}</p>
                    <div className="mt-0.5 flex items-center gap-2">
                      {m.criteria.length > 0 && (
                        <span className="flex flex-wrap items-center gap-0.5" aria-hidden="true">
                          {m.criteria.map((c) => (
                            <span key={c.id} className={`h-2 w-2 rounded-[2px] ${VERDICT_SQUARE[c.verdict] || VERDICT_SQUARE.unknown}`} />
                          ))}
                        </span>
                      )}
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {m.criteria.length === 0
                          ? 'No comparable criteria yet'
                          : `${m.passes} fit · ${m.unknowns} unknown${m.fails > 0 ? ` · ${m.fails} don't fit` : ''}`}
                      </span>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => start(m)}
                  disabled={startingId !== null}
                  className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                >
                  {starting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                  {m.resume ? 'Resume prescreen' : 'Start prescreen'}
                </button>
              </div>
              {isOpen && (
                <ul className="ml-6 mt-1 divide-y divide-gray-50 dark:divide-gray-700/50">
                  {m.criteria.length === 0
                    ? <li className="py-1.5 text-xs text-gray-400 dark:text-gray-500">This study's criteria aren't mapped to the shared vocabulary yet — run the prescreen to find out.</li>
                    : m.criteria.map((c) => <CriterionRow key={c.id} c={c} />)}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
